import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import "../modal.css";
import Autocomplete from "@mui/material/Autocomplete";
import { BASE_URL } from "../../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";
import { checkIsSuperAdmin } from "../../Utility/utility";
import { InputBase } from "@mui/material";

export default function BusinessModalComponent({
  show,
  handleClose,
  onSave,
  editingData,
  title,
  isSaving = false,
}) {
  const [businessUnit, setBusinessUnit] = React.useState("");
  const [adminUrl, setAdminUrl] = React.useState("");
  const [instanceName, setInstanceName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [businessId, setBusinessId] = React.useState("");
  const [isDefaultChecked, setIsDefaultChecked] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const currentTimestamp = new Date();
  const seconds = currentTimestamp.getSeconds();
  const formattedTimestamp = `${currentTimestamp.getFullYear()}-${(currentTimestamp.getMonth() + 1).toString().padStart(2, "0")}-${currentTimestamp.getDate().toString().padStart(2, "0")} ${currentTimestamp.getHours().toString().padStart(2, "0")}:${currentTimestamp.getMinutes().toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  useEffect(() => {
    const checkAdminStatus = async () => {
      const isSuperAdminUser = await checkIsSuperAdmin();
      setIsSuperAdmin(isSuperAdminUser);

      if (isSuperAdminUser) {
        const orgResponse = await fetch(
          `${BASE_URL}/Organization/${getUserIdFromLocalStorage().userId}/${getUserIdFromLocalStorage().organizationId}`,
          { headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` } }
        );
        if (orgResponse.ok) {
          const orgData = await orgResponse.json();
          setOrganizations(orgData.filter((org) => org && org.id && org.name).reverse());
        }
      } else {
        const userDataJSON = localStorage.getItem("token");
        if (userDataJSON) {
          const userData = JSON.parse(userDataJSON);
          const usersResponse = await fetch(
            `${BASE_URL}/users/getAllUsers/${userData.organizationId}`,
            { headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` } }
          );
          if (usersResponse.ok) {
            // users fetched but no longer shown in this modal
          }
        }
      }
    };
    checkAdminStatus();
  }, []);

  React.useEffect(() => {
    if (editingData) {
      setBusinessUnit(editingData.businessUnit);
      setAdminUrl(editingData.adminUrl || "");
      setInstanceName(editingData.instanceName || "");
      setDescription(editingData.description || "");
      setBusinessId(editingData.id);
      setIsDefaultChecked(editingData.isDefault);
      if (isSuperAdmin && editingData.organizationId) {
        const org = organizations.find((o) => o.id === editingData.organizationId);
        if (org) setSelectedOrganization(org);
      }
    } else {
      setBusinessUnit("");
      setAdminUrl("");
      setInstanceName("");
      setDescription("");
      setBusinessId(formattedTimestamp);
      setIsDefaultChecked(false);
      setSelectedOrganization(null);
    }
    // Re-run whenever the modal opens (not just when editingData changes) so a fresh
    // "Create New" session never shows fields left over from a previous create/edit.
  }, [show, editingData, organizations, isSuperAdmin]);

  const handleSaveClick = () => {
    if (!businessUnit.trim()) {
      alert("Client is required.");
      return;
    }
    if (!adminUrl.trim()) {
      alert("Admin URL is required.");
      return;
    }
    const newRow = {
      id: businessId,
      businessUnit,
      adminUrl,
      instanceName,
      description,
      isDefaultChecked,
      organizationId: isSuperAdmin ? selectedOrganization?.id : null,
    };
    onSave(newRow);
  };

  return (
    <Modal
      open={show}
      onClose={isSaving ? undefined : handleClose}
      aria-labelledby="add-administrator-modal-title"
    >
      <div className="user-modal modal-container">
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          <span className="modal-title" style={{ fontWeight: 500 }}>
            {title}
          </span>
          <button
            onClick={handleClose}
            className="modal-close-btn"
            aria-label="Close"
            disabled={isSaving}
          >
            &#x2715;
          </button>
        </div>

        <div>
          {/* Client Name */}
          <div className="modal-field-wrap">
            <label className="modal-label">
              Client <span style={{ color: "red" }}>*</span>
            </label>
            <InputBase
              value={businessUnit}
              className="modal-input"
              onChange={(e) => setBusinessUnit(e.target.value)}
              inputProps={{ style: { padding: 0 } }}
              placeholder="Name"
            />
          </div>

          {/* Admin URL */}
          <div className="modal-field-wrap">
            <label className="modal-label">
              Admin URL <span style={{ color: "red" }}>*</span>
            </label>
            <InputBase
              value={adminUrl}
              className="modal-input"
              onChange={(e) => setAdminUrl(e.target.value)}
              inputProps={{ style: { padding: 0 } }}
              placeholder="Enter URL"
            />
          </div>
          {/* Instance Name */}
          <div className="modal-field-wrap">
            <label className="modal-label">Instance Name</label>
            <InputBase
              value={instanceName}
              className="modal-input"
              onChange={(e) => setInstanceName(e.target.value)}
              inputProps={{ style: { padding: 0 } }}
              placeholder="Enter instance name"
            />
          </div>

          {/* Description */}
          <div className="modal-field-wrap">
            <label className="modal-label">Description</label>
            <InputBase
              value={description}
              className="modal-input"
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ style: { padding: 0 } }}
              placeholder="Enter description"
            />
          </div>
          {/* Organization (super admin only) */}
          {isSuperAdmin && (
            <div className="modal-field-wrap">
              <label className="modal-label">Organization</label>
              <Autocomplete
                options={organizations}
                getOptionLabel={(option) => option.name || ""}
                value={selectedOrganization}
                onChange={(event, newValue) => setSelectedOrganization(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="Select organization..."
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      className: "modal-dropdown-input",
                      style: { width: "100%" },
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="No organizations found"
                fullWidth
              />
            </div>
          )}

          {/* Buttons */}
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "8px" }}
          >
            <Button
              variant="contained"
              onClick={handleClose}
              className="cancel-btn-filled"
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveClick}
              className="review-btn-filled"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
            >
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

BusinessModalComponent.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editingData: PropTypes.object,
  title: PropTypes.string.isRequired,
  isSaving: PropTypes.bool,
};
